import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";

// ─────────────────────────────────────────────────────────────────────────────
// Slab Intelligence-driven brand-wise discount validation (shared client logic).
// Mirrors the backend `_compute_slab_validation` in server.py — keep both in sync.
//
// Brand → Slab column mapping (confirmed with product owner):
//   Wellcore  → Wellcore Allowed Discount (no contribution requirement)
//   Dynamite  → contribution range = "Additional % – Dynamite", allowed = Other Brands
//   YouWeFit  → contribution range = "Additional % – Omega"   (Omega == YouWeFit), allowed = Other Brands
//   OSMO      → contribution range = "Additional % – OSMO",     allowed = Other Brands
//   Everything else (Okami, Zero Sugar, …) → Other Brands Allowed Discount, no contribution check
// ─────────────────────────────────────────────────────────────────────────────

export const BRAND_SLAB_MAP = {
  Wellcore: { kind: "wellcore", allowedField: "wellcore_discount", contribField: null },
  Dynamite: { kind: "conditional", allowedField: "other_brands_discount", contribField: "add_dynamite" },
  YouWeFit: { kind: "conditional", allowedField: "other_brands_discount", contribField: "add_omega" },
  OSMO: { kind: "conditional", allowedField: "other_brands_discount", contribField: "add_osmo" },
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// "61%" -> 61, "62.5%" -> 62.5, "—"/""/"N/A" -> null
export function parseSlabPct(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace("%", "").trim();
  if (!s || s === "—" || s === "-" || /^n\/?a$/i.test(s)) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

// "15%-25%" -> {min:15,max:25}, "5%-Above" -> {min:5,max:Infinity}, "—"/"" -> null
export function parseSlabRange(raw) {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).replace(/–|—/g, "-").replace(/%/g, "").trim();
  if (!s || s === "-" || /^n\/?a$/i.test(s)) return null;
  const parts = s.split("-").map((p) => p.trim()).filter((p) => p !== "");
  if (parts.length === 0) return null;
  const min = parseFloat(parts[0]);
  if (Number.isNaN(min)) return null;
  let max;
  if (parts.length < 2) {
    max = min;
  } else if (/above|more|\+/i.test(parts[1])) {
    max = Infinity;
  } else {
    max = parseFloat(parts[1]);
    if (Number.isNaN(max)) max = Infinity;
  }
  return { min, max };
}

export function findApplicableSlab(slabs, totalInvoiceValue) {
  const tiv = Number(totalInvoiceValue) || 0;
  for (const row of slabs || []) {
    const mn = Number(row.min_invoice_value) || 0;
    const mx = Number(row.max_invoice_value) || 0;
    if (tiv >= mn && tiv <= mx) return row;
  }
  return null;
}

const rangeMaxLabel = (max) => (max === Infinity ? "Above" : `${max}%`);

// brandStats: { [brand]: { invoice, discount } }  (discount = brand effective discount %)
// Returns { applicableSlab, brands:[...], requiresApproval, reasons }
export function computeSlabValidation({ brandStats, totalInvoiceValue, slabs }) {
  const tiv = Number(totalInvoiceValue) || 0;
  const slab = findApplicableSlab(slabs, tiv);
  const brands = [];
  const reasons = [];

  Object.entries(brandStats || {}).forEach(([brand, stats]) => {
    const cfg = BRAND_SLAB_MAP[brand] || { kind: "other", allowedField: "other_brands_discount", contribField: null };
    const contribution = tiv > 0 ? round2((Number(stats.invoice) || 0) / tiv * 100) : 0;
    const effective = round2(Number(stats.discount) || 0);
    const range = cfg.contribField ? parseSlabRange(slab ? slab[cfg.contribField] : null) : null;

    const result = {
      brand,
      contribution,
      effectiveDiscount: effective,
      requiredRange: range,
      requiredRangeLabel: range ? `${range.min}%–${rangeMaxLabel(range.max)}` : null,
      hasContributionRule: cfg.kind === "conditional",
      allowedDiscount: null,
      allowedLabel: "Not Defined",
      status: "within",
      reason: "",
    };

    if (!slab) {
      result.status = "approval";
      result.allowedLabel = "Not Defined";
      result.reason = "No applicable Slab Intelligence row for this invoice value";
      reasons.push(`${brand}: ${result.reason}`);
      brands.push(result);
      return;
    }

    if (cfg.kind === "conditional") {
      if (!range) {
        result.status = "approval";
        result.allowedLabel = "Not Defined";
        result.reason = "Required contribution range not defined for this slab";
        reasons.push(`${brand}: ${result.reason}`);
        brands.push(result);
        return;
      }
      const inRange = contribution >= range.min - 0.005 && contribution <= range.max + 0.005;
      if (!inRange) {
        result.status = "approval";
        result.allowedLabel = "Not Eligible";
        result.allowedDiscount = null;
        result.reason = `Contribution ${contribution.toFixed(2)}% is outside required ${result.requiredRangeLabel}`;
        reasons.push(`${brand}: ${result.reason}`);
        brands.push(result);
        return;
      }
    }

    // Eligible (or Wellcore / Other Brands) → compare effective vs allowed
    const allowedRaw = slab[cfg.allowedField];
    const allowed = parseSlabPct(allowedRaw);
    if (allowed === null) {
      result.status = "approval";
      result.allowedLabel = "Not Defined";
      result.reason = "Allowed Discount not defined for this slab";
      reasons.push(`${brand}: ${result.reason}`);
    } else {
      result.allowedDiscount = allowed;
      result.allowedLabel = String(allowedRaw).trim();
      if (effective > allowed + 0.005) {
        result.status = "approval";
        result.reason = `Effective Discount ${effective.toFixed(2)}% exceeds Allowed Discount ${result.allowedLabel}`;
        reasons.push(`${brand}: ${result.reason}`);
      } else {
        result.status = "within";
        result.reason = "Within Limit";
      }
    }
    brands.push(result);
  });

  return { applicableSlab: slab, brands, requiresApproval: reasons.length > 0, reasons };
}

// Fetch the Slab Intelligence slabs applicable to a quotation date (its quarter).
// Any authenticated user can read this — it is reference data needed to create quotations.
export function useApplicableSlab(orderDate) {
  const [data, setData] = useState({ slabs: [], quarterLabel: "", quarterId: "", status: "", loading: true });
  useEffect(() => {
    let alive = true;
    if (!orderDate) {
      setData((d) => ({ ...d, loading: false }));
      return;
    }
    setData((d) => ({ ...d, loading: true }));
    axios
      .get(`${API}/slab-intelligence/applicable`, {
        params: { date: orderDate },
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      .then((r) => {
        if (!alive) return;
        setData({
          slabs: r.data?.slabs || [],
          quarterLabel: r.data?.label || "",
          quarterId: r.data?.quarter_id || "",
          status: r.data?.status || "",
          loading: false,
        });
      })
      .catch(() => {
        if (alive) setData({ slabs: [], quarterLabel: "", quarterId: "", status: "", loading: false });
      });
    return () => {
      alive = false;
    };
  }, [orderDate]);
  return data;
}
